import React, { useMemo } from 'react';
import { Card, Col, Form, Row, Select, Switch, Tag, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import {
  calculateAgeMonths,
  formatSpeciesLifecycleSummary,
} from './core';
import { useSpeciesLifecycle } from './SpeciesLifecycleContext';

const { Option } = Select;
const { Text } = Typography;

export default function AgeClassificationForm({
  form,
  colProps = { xs: 24, sm: 12, md: 8, lg: 8 },
  categoryName = null,
  title,
  subtitle,
  showCard = true,
  fieldNames = {
    dateOfBirth: 'date_of_birth',
    ageMonthsFallback: 'age_months',
    sex: 'gender',
    weaned: 'is_weaned',
    dateWeaned: 'date_weaned',
    castrated: 'is_neutered',
    breedingStatus: 'breeding_status',
    status: 'status',
    ageClass: 'age_class',
  },
}) {
  const { t } = useTranslation();
  const { themeMode } = useTheme();
  const isDark = themeMode === 'dark';
  const { profiles, isReady: lifecycleConfigReady, computeAgeClass: computeAgeClassWithProfiles } =
    useSpeciesLifecycle();

  const sex = Form.useWatch(fieldNames.sex, form);
  const dateOfBirth = Form.useWatch(fieldNames.dateOfBirth, form);
  const ageMonthsFallback = Form.useWatch(fieldNames.ageMonthsFallback, form);
  const isWeaned = Form.useWatch(fieldNames.weaned, form);
  const dateWeaned = Form.useWatch(fieldNames.dateWeaned, form);
  const isCastrated = Form.useWatch(fieldNames.castrated, form);
  const breedingStatus = Form.useWatch(fieldNames.breedingStatus, form);
  const status = Form.useWatch(fieldNames.status, form);

  const ageMonths = useMemo(
    () => calculateAgeMonths({ dateOfBirth, fallbackAgeMonths: ageMonthsFallback }),
    [dateOfBirth, ageMonthsFallback]
  );

  const weaned = useMemo(() => {
    if (typeof isWeaned === 'boolean') return isWeaned;
    if (dateWeaned) return true;
    return null;
  }, [isWeaned, dateWeaned]);

  const computedAgeClass = useMemo(() => {
    const lactating = sex === 'female' && String(status || '').toLowerCase() === 'lactating';
    return computeAgeClassWithProfiles({
      ageMonths,
      categoryName,
      sex,
      weaned,
      castrated: sex === 'male' ? !!isCastrated : false,
      breedingStatus: sex === 'female' ? breedingStatus : null,
      lactating,
    });
  }, [
    ageMonths,
    categoryName,
    sex,
    weaned,
    isCastrated,
    breedingStatus,
    status,
    computeAgeClassWithProfiles,
  ]);

  const lifecycleTierHelp = useMemo(() => {
    if (!lifecycleConfigReady || !categoryName) return null;
    return formatSpeciesLifecycleSummary(categoryName, profiles);
  }, [lifecycleConfigReady, categoryName, profiles]);

  React.useEffect(() => {
    const current = form.getFieldValue(fieldNames.ageClass);
    if (computedAgeClass && current !== computedAgeClass) {
      form.setFieldsValue({ [fieldNames.ageClass]: computedAgeClass });
    }
    if (!computedAgeClass && current) {
      form.setFieldsValue({ [fieldNames.ageClass]: '' });
    }
  }, [computedAgeClass, form, fieldNames.ageClass]);

  const content = (
    <Row gutter={20}>
      <Col span={24} style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Text strong>{t('animals.ageClass', 'Age Class')}</Text>
          {computedAgeClass ? (
            <Tag color="geekblue" style={{ borderRadius: 10, fontWeight: 600 }}>
              {computedAgeClass}
            </Tag>
          ) : (
            <Tag style={{ borderRadius: 10 }}>{t('common.notSet', 'Not set')}</Tag>
          )}
          <Tooltip
            title={
              lifecycleTierHelp
                ? t(
                    'animals.ageClassFormulaHelpWithLifecycle',
                    'Age Class is computed from age, sex, weaning, castration, reproduction, and lactation. System lifecycle names for this species: {{names}}.',
                    { names: lifecycleTierHelp }
                  )
                : t(
                    'animals.ageClassFormulaHelp',
                    'Age Class is computed from age + sex + weaning + castration (khasi) + reproduction + lactation.'
                  )
            }
          >
            <InfoCircleOutlined style={{ color: isDark ? '#8c8c8c' : '#595959' }} />
          </Tooltip>
        </div>
      </Col>

      <Col {...colProps}>
        <Form.Item
          name={fieldNames.weaned}
          label={t('animals.weaned', 'Weaned')}
          valuePropName="checked"
        >
          <Switch checkedChildren={t('common.yes', 'Yes')} unCheckedChildren={t('common.no', 'No')} />
        </Form.Item>
      </Col>

      {sex === 'male' && (
        <Col {...colProps}>
          <Form.Item
            name={fieldNames.castrated}
            label={t('animals.khasiCastrated', 'Khasi (Castrated)')}
            valuePropName="checked"
          >
            <Switch checkedChildren={t('common.yes', 'Yes')} unCheckedChildren={t('common.no', 'No')} />
          </Form.Item>
        </Col>
      )}

      {sex === 'female' && (
        <Col {...colProps}>
          <Form.Item name={fieldNames.breedingStatus} label={t('animals.breedingStatus', 'Breeding Status')}>
            <Select allowClear placeholder={t('animals.selectBreedingStatus', 'Select status')}>
              <Option value="Open">{t('animals.breedingStatusOptions.open', 'Open')}</Option>
              <Option value="Exposed">{t('animals.breedingStatusOptions.exposed', 'Exposed')}</Option>
              <Option value="Pregnant">{t('animals.breedingStatusOptions.pregnant', 'Pregnant')}</Option>
            </Select>
          </Form.Item>
        </Col>
      )}

      <Col span={24} style={{ display: 'none' }}>
        <Form.Item name={fieldNames.ageClass}>
          <input />
        </Form.Item>
      </Col>
    </Row>
  );

  if (!showCard) return content;

  return (
    <Card
      className="form-section-card"
      style={{
        border: 'none',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        marginBottom: 24,
        background: isDark ? '#1f1f1f' : '#fff',
      }}
      bodyStyle={{ padding: 24 }}
    >
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
          {title || t('animals.classification', 'Classification')}
        </div>
        <div style={{ color: isDark ? 'rgba(255,255,255,0.55)' : '#8c8c8c', fontSize: 13 }}>
          {subtitle || t('animals.classificationSubtitle', 'Weaning, castration, breeding status, and computed age class')}
        </div>
      </div>
      {content}
    </Card>
  );
}
